import inputVariables from "@krystal/css/components/input.css?inline";
import { html, nothing, unsafeCSS } from "lit";
import { customElement, property } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { createRef, ref, type Ref } from "lit/directives/ref.js";
import { when } from "@/common/directives/when";
import "@/components/tooltip";
import { cn } from "@/utilities/css";
import EkoElement from "@/utilities/element";
import style from "./textarea.css?inline";

@customElement("eko-textarea")
export class Textarea extends EkoElement {
  static formAssociated = true;

  static styles = [...EkoElement.styles, unsafeCSS(inputVariables), unsafeCSS(style)];

  private _initialValueSet = false;

  private handleBlur = () => {
    this.dispatchEvent(new FocusEvent("blur", { bubbles: true }));
    this.checkValidity();
  };

  private handleFocus = () => {
    this.dispatchEvent(new FocusEvent("focus", { bubbles: true }));
  };

  private handleInput = (event: Event) => {
    const textarea = event.target as HTMLTextAreaElement;
    this.value = textarea.value;
    this.updateFormValue();
    this.checkValidity();

    this.dispatchEvent(
      new CustomEvent("input", {
        bubbles: true,
        detail: { value: textarea.value },
      })
    );
  };

  private inputRef: Ref<HTMLTextAreaElement> = createRef();

  private internals: ElementInternals;
  @property({ reflect: true, type: Boolean }) admin: boolean = false;
  @property() autocomplete: "on" | "off" = "on";
  @property() class: string = "";
  @property() description: string = "";
  @property({ reflect: true, type: Boolean }) disabled: boolean = false;
  @property() error: string = "";
  @property() help: string = "";
  @property() id: string = `textarea-${Math.random().toString(36).substr(2, 9)}`;
  @property() label: string = "";
  @property({ type: Number }) maxlength: number | null = null;
  @property({ type: Number }) minlength: number | null = null;
  @property() name: string = "";
  @property() placeholder: string = "";
  @property({ reflect: true, type: Boolean }) readonly: boolean = false;
  @property({ type: Boolean }) required: boolean = false;
  @property({ type: Number }) rows: number = 3;
  @property({ attribute: false }) value: string = "";

  constructor() {
    super();
    this.internals = this.attachInternals();
  }

  private updateFormValue() {
    const value = this.value;
    this.internals.setFormValue(value);
  }

  checkValidity(): boolean {
    const input = this.inputRef.value;
    if (!input) return true;

    // Use native validation first
    const isNativeValid = input.checkValidity();

    if (!isNativeValid) {
      this.internals.setValidity(input.validity, input.validationMessage, input);
      return false;
    }

    // Clear validation state if valid
    this.internals.setValidity({});
    return true;
  }

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  focus() {
    if (this.inputRef.value) {
      this.inputRef.value.focus();
    }
  }

  // Form lifecycle methods
  formAssociatedCallback() {
    // Called when associated with a form
  }

  formDisabledCallback(disabled: boolean) {
    this.disabled = disabled;
  }

  formResetCallback() {
    this.value = "";
    this.internals.setFormValue(null);
    this.internals.setValidity({});
  }

  formStateRestoreCallback(state: string | null) {
    this.value = state || "";
    if (state) {
      this.internals.setFormValue(state);
    } else {
      this.internals.setFormValue(null);
    }
  }

  render() {
    const descriptionId = this.description ? `${this.id}-description` : "";

    return html`
      <div class=${cn("eko-textarea", this.class)} part="root">
        ${this.label
          ? html`
              <eko-label
                for=${this.id}
                ?required=${this.required}
                help=${when(this.help)}
                error=${when(this.error)}
                ?admin=${this.admin}
                >${this.label}</eko-label
              >
            `
          : nothing}

        <div class="eko-textarea__wrapper" part="wrapper">
          <textarea
            ${ref(this.inputRef)}
            id=${this.id}
            name=${ifDefined(this.name)}
            placeholder=${ifDefined(this.placeholder)}
            ?required=${this.required}
            ?disabled=${this.disabled}
            autocomplete=${this.autocomplete}
            rows=${this.rows}
            maxlength=${ifDefined(this.maxlength ?? undefined)}
            minlength=${ifDefined(this.minlength ?? undefined)}
            ?readonly=${this.readonly}
            aria-describedby=${descriptionId}
            class="eko-textarea__field"
            .value=${this.value}
            part="field"
            @input=${this.handleInput}
            @focus=${this.handleFocus}
            @blur=${this.handleBlur}></textarea>
        </div>
        ${this.hasError || this.description
          ? html`<div>
              ${this.hasError
                ? html`<div class="eko-textarea__error" part="error">${this.error}</div>`
                : nothing}
              ${this.description
                ? html`
                    <div id=${descriptionId} class="eko-textarea__description" part="description">
                      ${this.description}
                    </div>
                  `
                : nothing}
            </div> `
          : nothing}
      </div>
    `;
  }

  reportValidity(): boolean {
    return this.internals.reportValidity();
  }

  updated(changedProperties: Map<string, any>) {
    if (changedProperties.has("value")) {
      this.updateFormValue();
    }

    if (changedProperties.has("disabled")) {
      this.internals.ariaDisabled = this.disabled ? "true" : "false";
    }

    if (changedProperties.has("required")) {
      this.checkValidity();
    }
  }

  willUpdate(changedProperties: Map<string, any>) {
    super.willUpdate(changedProperties);

    if (!this._initialValueSet && !this.value && this.textContent) {
      this.value = this.textContent.trim();
      this._initialValueSet = true;
    }
  }

  get form() {
    return this.internals.form;
  }

  private get hasError(): boolean {
    return this.error !== "";
  }

  get validationMessage(): string {
    return this.internals.validationMessage;
  }

  get validity(): globalThis.ValidityState {
    return this.internals.validity;
  }

  get willValidate(): boolean {
    return !this.disabled;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-textarea": Textarea;
  }
}
